// A row's actions: the Buttons kebab, or a trigger with the consumer's visible label, opening one menu of
// commands and destinations. A row with no actions renders no trigger. When every action conflicts with bulk
// work the trigger stays in place, focusable and aria-disabled, and explains why.

import React, { useContext, useId } from 'react'
import PropTypes from 'prop-types'

import { Button, Menu } from '@CommonComponents/buttons'
import { TableContext } from '../context'
import { tierOf } from '../geometry/controlSize'
import { ROW_ACTIONS_LABEL } from './copy'
import { blockedState, isUsableAction, menuItem, menuWidth } from './menuItems'

const KEBAB = 'fa-regular fa-ellipsis-vertical'
// the kebab's size tier comes from the geometry that sizes the actions column
const iconTrigger = density =>
  `me-btn-icon me-btn-icon-${tierOf('actions', density)} me-table-row-actions`
const LABEL_TRIGGER =
  'me-btn me-btn-secondary me-btn-xs me-btn-menu-trigger me-table-row-actions'

const isText = value => typeof value === 'string' && value.trim() !== ''

const BlockedTrigger = ({ label, reason, density }) => {
  const reasonId = useId()
  return (
    <>
      <Button
        className={label ? LABEL_TRIGGER : iconTrigger(density)}
        icon={label ? undefined : KEBAB}
        iconAfter={
          label ? 'fa-regular fa-chevron-down me-btn-caret' : undefined
        }
        aria-label={label || ROW_ACTIONS_LABEL}
        aria-disabled
        aria-describedby={reason ? reasonId : undefined}
        data-tooltip={label ? reason || undefined : ROW_ACTIONS_LABEL}
        data-tooltip-explanation={label ? undefined : reason || undefined}
      >
        {label || undefined}
      </Button>
      {reason && (
        <span id={reasonId} className="me-btn-sr-only">
          {reason}
        </span>
      )}
    </>
  )
}

BlockedTrigger.propTypes = {
  label: PropTypes.string,
  reason: PropTypes.string,
  density: PropTypes.string,
}

export const RowActions = ({ actions, row, label, minMenuWidth }) => {
  const table = useContext(TableContext)
  const reasonId = useId()
  const config = (table && table.config) || {}
  const density =
    table && table.state && table.state.view && table.state.view.density
  const width = menuWidth(minMenuWidth || config.minMenuWidth)

  let declared = actions
  if (!declared && row && table && typeof table.rowActions === 'function')
    declared = table.rowActions(row)
  const usable = (declared || []).filter(isUsableAction)
  if (!usable.length) return null

  const shown = isText(label)
    ? label
    : isText(config.rowActionsLabel)
    ? config.rowActionsLabel
    : null
  const states = usable.map(action =>
    blockedState(table && table.rowActionState, action)
  )
  if (states.every(state => state.blocked))
    return (
      <BlockedTrigger
        label={shown}
        density={density}
        reason={states.find(state => state.reason)?.reason || null}
      />
    )

  return (
    <Menu
      label={shown || ROW_ACTIONS_LABEL}
      iconOnly={!shown}
      icon={KEBAB}
      className={shown ? LABEL_TRIGGER : iconTrigger(density)}
      align="end"
      minWidth={width}
    >
      {usable.map((action, index) =>
        menuItem(action, index, states[index], reasonId)
      )}
    </Menu>
  )
}

export const rowActionShape = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  href: PropTypes.string,
  icon: PropTypes.string,
  hint: PropTypes.string,
  danger: PropTypes.bool,
  disabled: PropTypes.bool,
})

RowActions.propTypes = {
  // the row's actions; when omitted they come from the table's rowActions(row)
  actions: PropTypes.arrayOf(rowActionShape),
  row: PropTypes.object,
  // the row identity, for callers that name the row elsewhere
  identity: PropTypes.string,
  // a visible trigger label in place of the kebab
  label: PropTypes.string,
  minMenuWidth: PropTypes.number,
}
